import {
    CustodianSettingsState,
    EmployerCustodianSettings,
    FetchState,
    FetchStateKey,
    UserCustodianSettings
} from "@/shared/types";
import * as BankService from "@/shared/api/bankService";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import * as custodianSettingsHelper from "@/shared/custodianSettingsHelper";
import { BankCustodian } from "@/admin/types";
import { AxiosResponse } from "axios";
import { fetchStateKeyMatch } from "../fetchStateHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import moment from "moment";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import { useUserStore } from "@/shared/store/user";

export const initialState: CustodianSettingsState = {
    employerCustodianSettings: {
        fetchState: FetchState.Uninitialized,
        suppressInviteEmailLink: false,
        suppressEmployerOnboardingBankInfo: false,
        employerCustodian: BankCustodian.None,
        currentEmployerId: null,
        validateEmployeeEmailLength: true,
        disableEmployeeEdit: false,
        employerCustodianInfoTemplate: "",
        displayBankAccountHolds: false
    },
    userCustodianSettings: {
        userHsaCustodian: BankCustodian.None,
        footerTemplate: "",
        investmentRiskDisclaimer: "",
        taxLink: null,
        statementsLink: null,
        statementPreferencesLink: null,
        faqTemplate: "",
        faqInstructionsTemplate: "",
        displayMyProfileEditMode: true,
        allowEmailEdit: true,
        displayInvestmentsSection: true,
        profileEditInformationHtml: null,
        logoutRedirectUrl: null,
        displayFooterLegalSection: true,
        contactDetailsSetAtBank: false,
        displayAdvice: true,
        displayFaqExternalLinks: false,
        displaySettingsLinkedAccounts: true,
        priorYearContributionOption: true,
        allowReimbursement: true,
        showPendingReimbursements: false,
        displayFaqInstructions: false,
        showAlternateOnboardingCompleteMessage: false,
        fetchState: FetchState.Uninitialized,
        displayMFASettings: true,
        termsUrl: "",
        hsaTermsUrl: "",
        feeTemplate: "TermsFeeSchedule",
        isFeeTemplateExternalUrl: false,
        cashAccountFooter: null,
        contributionsFooter: null,
        quarterlyPaperStatementFee: null,
        manageHsaCardsInPortal: null,
        displayPriorYearContributionDisclaimerCopy: false,
        fetchStateKey: null,
        displayProjectedContributions: true,
        allowUserToEditContributions: true,
        showCashExpensesGraphWidget: true,
        showExpenseCategoryWidget: true,
        showExpenseEligibilityWidget: true,
        showHealthcareExpensesV2: true,
        displayHealthcareClaims: true,
        displayPdfForESign: false,
        displayHealthcareBluebookLink: false,
        faqLinkAddress: "",
        eSignPdfUrl: "",
        allowFamilyMemberSsnInput: false,
        showHsaStoreShopTab: true,
        showChildExpenseRows: true,
        transactionPayrollOneTimeIsAdjustment: false,
        disabledRouteRedirects: {},
        displayCashAccountMfaPopup: false,
        displayNewHealthcareExpenses: true,
        disableHsaInvestEnrollButtonAfterDate: "",
        disablePortalFunctionalityAfterDate: "",
        displayFdicBanner: false,
        fdicDisclaimer: "",
        transferOrRolloverHsaFundsUrl: "",
        loginHelpUrl: "",
        contactUsUrl: ""
  }
};

const maxAttempts = 9;
const userHsaCustodianRetry = (getFn: () => Promise<AxiosResponse<UserCustodianSettings>>,
                               resolve: (v: UserCustodianSettings) => void,
                               reject: (error: any) => void, attempts?: number) => {

    attempts = attempts || 0;
    if (attempts > maxAttempts) {
        reject("User not found");
    }
    const delayInMs = 1000 + (attempts * 3000);
    getFn()
        .then(v => resolve(v.data))
        .catch(err => {
            if (err && err.response && err.response.status === 404) {
                window.setTimeout(userHsaCustodianRetry.bind(undefined, getFn, resolve, reject, ++attempts), delayInMs);
                return;
            }
            reject(err);
        });
};

export const useCustodianSettingsStore = defineStore("custodianSettings", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("custodianSettings"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        fetchState: state => state.userCustodianSettings.fetchState,
        isLoaded () {
            const featureFlagStore = useFeatureFlagStore();
            return this.userCustodianSettings.fetchState === FetchState.Loaded &&
                featureFlagStore.featureFlagsAvailable;
        },
        logoutRedirectUrl: state => state.userCustodianSettings.logoutRedirectUrl,
        footerTemplate: state => state.userCustodianSettings.footerTemplate,
        investmentRiskDisclaimer: state => state.userCustodianSettings.investmentRiskDisclaimer,
        displayEmployerOnboardingBankInformation: state => state.employerCustodianSettings.suppressEmployerOnboardingBankInfo ===
            false,
        displayEmployerEmployeeInviteLink: state => state.employerCustodianSettings.suppressInviteEmailLink === false,
        statementsLink: state => state.userCustodianSettings.statementsLink,
        statementPreferencesLink: state => state.userCustodianSettings.statementPreferencesLink,
        taxLink: state => state.userCustodianSettings.taxLink,
        faqTemplate: state => state.userCustodianSettings.faqTemplate,
        faqInstructionsTemplate: state => state.userCustodianSettings.faqInstructionsTemplate,
        employerCustodianInfoTemplate: state => state.employerCustodianSettings.employerCustodianInfoTemplate,
        displayMyProfileEditMode: state => state.userCustodianSettings.displayMyProfileEditMode,
        displayMFASettings: state => state.userCustodianSettings.displayMFASettings,
        displayFaqExternalLinks: state => state.userCustodianSettings.displayFaqExternalLinks,
        displayFooterLegalSection: state => state.userCustodianSettings.displayFooterLegalSection,
        contactDetailsSetAtBank: state => state.userCustodianSettings.contactDetailsSetAtBank,
        displaySettingsLinkedAccounts: state => state.userCustodianSettings.displaySettingsLinkedAccounts,
        displayAdvice: state => state.userCustodianSettings.displayAdvice,
        transactionPayrollOneTimeIsAdjustment: state => state.userCustodianSettings.transactionPayrollOneTimeIsAdjustment,
        displayCashAccountMfaPopup: state => state.userCustodianSettings.displayCashAccountMfaPopup,
        transferOrRolloverHsaFundsUrl: state => state.userCustodianSettings.transferOrRolloverHsaFundsUrl,
        contactUsUrl: state => state.userCustodianSettings.contactUsUrl,

        disabledRoutes () {
            if (this.userCustodianSettings && this.userCustodianSettings.disabledRouteRedirects) {
                return this.userCustodianSettings.disabledRouteRedirects;
            }
            // we used to disable some routes, but no longer do.  maybe we will again?
            return {};
        },

        allowEmailEdit: state => state.userCustodianSettings.allowEmailEdit,
        profileEditInformationHtml () {
            const partnerSupportPhoneTemplateProperty = "{{partnerSupportPhone}}";
            const portalCustomizationStore = usePortalCustomizationStore();
            const containsTemplateProperty = this.userCustodianSettings.profileEditInformationHtml
                .indexOf(partnerSupportPhoneTemplateProperty) > -1;
            if (containsTemplateProperty) {
                const formattedCopy = this.userCustodianSettings.profileEditInformationHtml
                    .replace(partnerSupportPhoneTemplateProperty,
                        portalCustomizationStore.partnerSupportPhone);

                return formattedCopy;
            }

            return this.userCustodianSettings.profileEditInformationHtml;
        },
        priorYearContributionOption: state => state.userCustodianSettings.priorYearContributionOption,
        allowReimbursement: state => state.userCustodianSettings.allowReimbursement,
        showPendingReimbursements: state => state.userCustodianSettings.showPendingReimbursements,
        displayFaqInstructions: state => state.userCustodianSettings.displayFaqInstructions,
        displayAlternateCompleteMessage: state => state.userCustodianSettings.showAlternateOnboardingCompleteMessage,
        isHsaBankCustodian () {
            const featureFlag = useFeatureFlagStore();
            return featureFlag.featureFlagsAvailable && this.userCustodianSettings.userHsaCustodian ===
                BankCustodian.HsaBank;
        },
        displayTdaSettings: state => state.employerCustodianSettings.employerCustodian === BankCustodian.Elements,
        validateEmployeeEmailLength: state => state.employerCustodianSettings.validateEmployeeEmailLength,
        disableEmployeeEdit: state => state.employerCustodianSettings.disableEmployeeEdit,
        allowMfa: state => state.userCustodianSettings.userHsaCustodian !== BankCustodian.Elements,
        displayAvidiaOptions: state => state.employerCustodianSettings.employerCustodian === BankCustodian.Avidia,
        custodianSupportsUserPreferences: state => state.userCustodianSettings.userHsaCustodian ===
            BankCustodian.Avidia ||
            state.userCustodianSettings.userHsaCustodian === BankCustodian.HsaBank,
        displayClosedInvestmentAccountsList: state => state.userCustodianSettings.userHsaCustodian ===
            BankCustodian.Avidia ||
            state.userCustodianSettings.userHsaCustodian === BankCustodian.Allegacy ||
            state.userCustodianSettings.userHsaCustodian === BankCustodian.Elements,
        isElementsCustodian: state => state.userCustodianSettings.userHsaCustodian === BankCustodian.Elements,
        termsUrl: state => state.userCustodianSettings.termsUrl,
        hsaTermsUrl: state => state.userCustodianSettings.hsaTermsUrl,
        hsaTermsUrlOrDefault: state => state.userCustodianSettings.hsaTermsUrl ?
            state.userCustodianSettings.hsaTermsUrl : "/bend_financial_terms_of_service 20181009.pdf",
        feeTemplate: state => state.userCustodianSettings.feeTemplate,
        cashAccountFooter: state => state.userCustodianSettings.cashAccountFooter,
        contributionsFooter: state => state.userCustodianSettings.contributionsFooter,
        quarterlyPaperStatementFee: state => state.userCustodianSettings.quarterlyPaperStatementFee,
        manageHsaCardsInPortal: state => state.userCustodianSettings.manageHsaCardsInPortal,
        employerCustodian: state => state.employerCustodianSettings.employerCustodian,
        isEmployerCustodianLoaded: state => (employerId: number): boolean => {
            return state.employerCustodianSettings.fetchState === FetchState.Loaded
                   && state.employerCustodianSettings.currentEmployerId === employerId;
        },
        userCustodian: state => state.userCustodianSettings.userHsaCustodian,
        userCustodianName: state => custodianSettingsHelper.custodianDisplayName(
            state.userCustodianSettings.userHsaCustodian),
        displayPriorYearContributionDisclaimerCopy:
                state => state.userCustodianSettings.displayPriorYearContributionDisclaimerCopy,
        displayProjectedContributions: state => state.userCustodianSettings.displayProjectedContributions,
        allowUserToEditContributions: state => state.userCustodianSettings.allowUserToEditContributions,
        showCashExpensesGraphWidget: state => state.userCustodianSettings.showCashExpensesGraphWidget,
        showExpenseCategoryWidget: state => state.userCustodianSettings.showExpenseCategoryWidget,
        showExpenseEligibilityWidget: state => state.userCustodianSettings.showExpenseEligibilityWidget,
        showHealthcareExpensesV2: state => state.userCustodianSettings.showHealthcareExpensesV2,
        displayHealthcareClaims: state => state.userCustodianSettings.displayHealthcareClaims,
        displayPdfForESign: state => state.userCustodianSettings.displayPdfForESign,
        displayHealthcareBluebookLink: state => state.userCustodianSettings.displayHealthcareBluebookLink,
        faqLinkAddress: state => state.userCustodianSettings.faqLinkAddress,
        loginHelpUrl: state => state.userCustodianSettings.loginHelpUrl,
        eSignPdfUrl: state => state.userCustodianSettings.eSignPdfUrl,
        eSignPdfUrlOrDefault: state => state.userCustodianSettings.displayPdfForESign ?
            state.userCustodianSettings.eSignPdfUrl : "inline-content-esign-disclosure-as-of-2021-07-13",
        isFeeTemplateExternalUrl: state => state.userCustodianSettings.isFeeTemplateExternalUrl,
        allowFamilyMemberSsnInput: state => state.userCustodianSettings.allowFamilyMemberSsnInput,
        userHsaCustodian: state => state.userCustodianSettings.userHsaCustodian,
        showHsaStoreShopTab: state => state.userCustodianSettings.showHsaStoreShopTab,
        showChildExpenseRows: state => state.userCustodianSettings.showChildExpenseRows,
        disableHsaInvestEnrollButtonAfterDate: state => state.userCustodianSettings.disableHsaInvestEnrollButtonAfterDate,
        hsaInvestEnrollButtonDisabled (): boolean {
            if (!this.userCustodianSettings.disableHsaInvestEnrollButtonAfterDate) {
                return false;
            }

            const dateNow = moment();
            const disableHsaInvestEnrollButtonAfterDate = moment(this.userCustodianSettings.disableHsaInvestEnrollButtonAfterDate);

            return dateNow.isAfter(disableHsaInvestEnrollButtonAfterDate);
        },
        displayBankAccountHolds: state => state.employerCustodianSettings.displayBankAccountHolds,
        displayNewHealthcareExpenses: state => state.userCustodianSettings.displayNewHealthcareExpenses,
        disablePortalFunctionalityAfterDate: state => state.userCustodianSettings.disablePortalFunctionalityAfterDate,
        portalFunctionalityDisabled  (): boolean {
            if (!this.userCustodianSettings.disablePortalFunctionalityAfterDate) {
                return false;
            }

            const dateNow = moment();
            const disableFunctionalityDate = moment(this.userCustodianSettings.disablePortalFunctionalityAfterDate);

            return dateNow.isAfter(disableFunctionalityDate);
        },
        displayFdicBanner: state => state.userCustodianSettings.displayFdicBanner,
        fdicDisclaimer: state => state.userCustodianSettings.fdicDisclaimer
    },
    actions: {
        setUserCustodianFetchState (fetchState: FetchState) {
            this.userCustodianSettings.fetchState = fetchState;
        },
        setEmployerCustodianFetchState (fetchState: FetchState) {
            this.employerCustodianSettings.fetchState = fetchState;
        },
        setUserCustodianSettings (custodian: UserCustodianSettings) {
            this.userCustodianSettings.userHsaCustodian = custodian.userHsaCustodian;
            const featureFlagStore = useFeatureFlagStore();
            featureFlagStore.setUserContext({ custodian: custodian.userHsaCustodian });
            switch (this.userCustodianSettings.userHsaCustodian) {
                case BankCustodian.Allegacy: {
                    return custodianSettingsHelper.SetAllegacyUserSettings(this);
                }
                case BankCustodian.Umb: {
                    return custodianSettingsHelper.SetUmbUserSettings(this);
                }
                case BankCustodian.Elements: {
                    return custodianSettingsHelper.SetElementsUserSettings(this);
                }
                case BankCustodian.Ucb: {
                    return custodianSettingsHelper.SetUcbUserSettings(this);
                }
                case BankCustodian.Avidia: {
                    return custodianSettingsHelper.SetAvidiaUserSettings(this);
                }
                case BankCustodian.HsaBank: {
                    return custodianSettingsHelper.SetHsaBankUserSettings(this);
                }
                case BankCustodian.Omnibus: {
                    return custodianSettingsHelper.SetOmnibusUserSettings(this);
                }
                default: {
                    return custodianSettingsHelper.SetUmbUserSettings(this);
                }
            }
        },
        setEmployerCustodianSettings (custodian: EmployerCustodianSettings) {
            this.employerCustodianSettings.employerCustodian = custodian.employerCustodian;
            const featureFlagStore = useFeatureFlagStore();
            featureFlagStore.setUserContext({ custodian: custodian.employerCustodian });
            switch (custodian.employerCustodian) {
                case BankCustodian.Allegacy: {
                    return custodianSettingsHelper.SetAllegacyEmployerSettings(this);
                }
                case BankCustodian.Umb: {
                    return custodianSettingsHelper.SetUmbEmployerSettings(this);
                }
                case BankCustodian.Elements: {
                    return custodianSettingsHelper.SetElementsEmployerSettings(this);
                }
                case BankCustodian.Ucb: {
                    return custodianSettingsHelper.SetUcbEmployerSettings(this);
                }
                case BankCustodian.Avidia: {
                    return custodianSettingsHelper.SetAvidiaEmployerSettings(this);
                }
                case BankCustodian.HsaBank: {
                    return custodianSettingsHelper.SetHsaBankEmployerSettings(this);
                }
                case BankCustodian.Omnibus: {
                    return custodianSettingsHelper.SetOmnibusEmployerSettings(this);
                }
                default: {
                    return custodianSettingsHelper.SetUmbEmployerSettings(this);
                }
            }
        },
        setFetchStateKey (fetchStateKey: FetchStateKey) {
            this.userCustodianSettings.fetchStateKey = fetchStateKey;
        },
        getCustodianSettings (
            args?: {
                username?: string, partnerId?: number, force: boolean, loginPartnerId?: number
            }): Promise<any> {

            const forceUpdate = (args && args.force);
            const loginPartnerId = args && args.loginPartnerId ? args.loginPartnerId : 0;
            const fetchStateKey: FetchStateKey = {
                username: args ? args.username : "",
                partnerId: args ? args.partnerId : 0
            };

            if (!forceUpdate && this.userCustodianSettings.fetchState !== FetchState.Uninitialized &&
                fetchStateKeyMatch(this.userCustodianSettings.fetchStateKey, fetchStateKey)) {
                return Promise.resolve();
            }

            const isCustodianAlreadyLoaded = (
                this.userCustodianSettings.fetchState === FetchState.Loaded &&
                this.userCustodianSettings.userHsaCustodian !== BankCustodian.None &&
                fetchStateKeyMatch(this.userCustodianSettings.fetchStateKey, fetchStateKey)
            );

            let resultPromiseWrapper: (result: UserCustodianSettings) => Promise<UserCustodianSettings>;
            if (isCustodianAlreadyLoaded) {
                resultPromiseWrapper = result => {
                    if (result.userHsaCustodian === this.userCustodianSettings.userHsaCustodian) {
                        return Promise.resolve(result); // trivial promise
                    }

                    // changing the custodian, simulate an API call to avoid changing branding without warning the user
                    return new Promise(action => {
                        this.setUserCustodianFetchState(FetchState.Loading);
                        return setTimeout(action, 500, result);
                    });
                };
            } else {
                resultPromiseWrapper = result => Promise.resolve(result); // trivial promise
                this.setUserCustodianFetchState(FetchState.Loading);
            }

            const apiCallPromiseFcn = userHsaCustodianRetry.bind(undefined,
                () => BankService.getUserHsaCustodian(fetchStateKey.username, fetchStateKey.partnerId, loginPartnerId));

            return new Promise<UserCustodianSettings>(apiCallPromiseFcn)
                .then(result => resultPromiseWrapper(result).then(result => {
                    this.setUserCustodianSettings(result);
                    this.setUserCustodianFetchState(FetchState.Loaded);
                    this.setFetchStateKey(fetchStateKey);
                }))
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },

        getEmployerCustodian (employerId?: number): Promise<any> {

            if (this.employerCustodianSettings.fetchState !== FetchState.Uninitialized
                && this.employerCustodianSettings.currentEmployerId === employerId) {
                return Promise.resolve();
            }
            this.setEmployerCustodianFetchState(FetchState.Loading);

            const userStore = useUserStore();
            return BankService.getEmployerCustodian(employerId || userStore.employerId)
                .then(result => {
                    this.setEmployerCustodianSettings(result.data);
                    this.setEmployerCustodianFetchState(FetchState.Loaded);
                    this.employerCustodianSettings.currentEmployerId = employerId;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },

        logEmployeeHsaStoreClick (): Promise<any> {
            return UserService.logEmployeeHsaStoreClick();
        },
        logEmployeeHsaBlueBookClick (): Promise<any> {
            return UserService.logEmployeeHsaBlueBookClick();
        },
        logHsaStoreWidgetClick (isDefaultLink: boolean): Promise<any> {
            return UserService.logHsaStoreWidget(isDefaultLink);
        },
        getCustodianSettingsForPartner (partnerId: number): Promise<any> {
            const fetchStateKey: FetchStateKey = {
                partnerId
            };

            if (this.userCustodianSettings.fetchState !== FetchState.Uninitialized &&
              fetchStateKeyMatch(this.userCustodianSettings.fetchStateKey, fetchStateKey)) {
                return Promise.resolve();
            }

            this.setUserCustodianFetchState(FetchState.Loading);

            return BankService.getUserHsaCustodianByPartner(partnerId)
              .then(result => {
                  this.setUserCustodianSettings(result.data);
                  this.setUserCustodianFetchState(FetchState.Loaded);
                  this.setFetchStateKey(fetchStateKey);
              })
              .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadDefaultUserCustodianSettings (custodian: BankCustodian) {
            this.setUserCustodianSettings({ userHsaCustodian: custodian });
            this.setUserCustodianFetchState(FetchState.Loaded);
        },
    }
});

